import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './DiseasePrediction.css';
import { Container } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faShieldHalved, faDroplet, faHeartPulse, faGauge,
  faWeightScale, faLungs, faBrain, faFlask, faBolt, faDna,
  faChevronDown, faPlayCircle, faSpinner, faExclamationTriangle,
  faRotateRight, faCalendarDays, faRobot, faCheckCircle,
  faListCheck, faChartLine, faMicrochip, faLightbulb,
  faTriangleExclamation, faCircleInfo, faScaleBalanced,
  faArrowUp, faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import axiosInstance from '../../api/axiosInstance';
import { API_ENDPOINTS } from '../../api/config';
import BackButton from '../../components/BackButton/BackButton';


/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface DiseaseDetail {
  score: number;
  category: string;
  method: string;
  factors: string[];
  weight?: number;
  weighted_score?: number;
  contribution?: number;
}

interface DiseaseRiskProfile {
  id: number;
  overall_risk_score: number;
  overall_risk_category: string;
  prediction_method: string;
  ml_confidence: number | null;
  disease_details: Record<string, DiseaseDetail>;
  disease_weights?: Record<string, number>;
  total_weight?: number;
  ai_explanation: string;
  ai_recommendations: string[];
  diabetes_risk: number | null;
  cardiovascular_risk: number | null;
  hypertension_risk: number | null;
  obesity_risk: number | null;
  respiratory_risk: number | null;
  mental_health_risk: number | null;
  liver_risk: number | null;
  kidney_risk: number | null;
  thyroid_risk: number | null;
  metabolic_syndrome_risk: number | null;
  created_at: string;
}

interface HistoryItem {
  id: number;
  overall_risk_score: number;
  overall_risk_category: string;
  prediction_method: string;
  created_at: string;
}


/* ------------------------------------------------------------------ */
/*  Disease metadata                                                   */
/* ------------------------------------------------------------------ */

interface DiseaseConfig {
  key: string;
  riskField: keyof DiseaseRiskProfile;
  label: string;
  icon: IconDefinition;
  color: string;
  bgColor: string;
}

const DISEASES: DiseaseConfig[] = [
  { key: 'diabetes',             riskField: 'diabetes_risk',             label: 'Diabetes',             icon: faDroplet,     color: '#06b6d4', bgColor: 'rgba(6,182,212,0.1)' },
  { key: 'cardiovascular',      riskField: 'cardiovascular_risk',      label: 'Cardiovascular',       icon: faHeartPulse,  color: '#ef4444', bgColor: 'rgba(239,68,68,0.1)' },
  { key: 'hypertension',        riskField: 'hypertension_risk',        label: 'Hypertension',         icon: faGauge,       color: '#f97316', bgColor: 'rgba(249,115,22,0.1)' },
  { key: 'obesity',             riskField: 'obesity_risk',             label: 'Obesity',              icon: faWeightScale, color: '#f59e0b', bgColor: 'rgba(245,158,11,0.1)' },
  { key: 'respiratory',         riskField: 'respiratory_risk',         label: 'Respiratory',          icon: faLungs,       color: '#3b82f6', bgColor: 'rgba(59,130,246,0.1)' },
  { key: 'mental_health',       riskField: 'mental_health_risk',       label: 'Mental Health',        icon: faBrain,       color: '#8b5cf6', bgColor: 'rgba(139,92,246,0.1)' },
  { key: 'liver',               riskField: 'liver_risk',               label: 'Liver',                icon: faFlask,       color: '#10b981', bgColor: 'rgba(16,185,129,0.1)' },
  { key: 'kidney',              riskField: 'kidney_risk',              label: 'Kidney',               icon: faDroplet,     color: '#ec4899', bgColor: 'rgba(236,72,153,0.1)' },
  { key: 'thyroid',             riskField: 'thyroid_risk',             label: 'Thyroid',              icon: faBolt,        color: '#eab308', bgColor: 'rgba(234,179,8,0.1)' },
  { key: 'metabolic_syndrome',  riskField: 'metabolic_syndrome_risk',  label: 'Metabolic Syndrome',   icon: faDna,         color: '#6366f1', bgColor: 'rgba(99,102,241,0.1)' },
];

const RISK_THRESHOLDS = [
  { min: 0, max: 25, label: 'Low', color: '#10b981', bgColor: 'rgba(16,185,129,0.1)' },
  { min: 26, max: 50, label: 'Moderate', color: '#f59e0b', bgColor: 'rgba(245,158,11,0.1)' },
  { min: 51, max: 75, label: 'High', color: '#f97316', bgColor: 'rgba(249,115,22,0.1)' },
  { min: 76, max: 100, label: 'Critical', color: '#ef4444', bgColor: 'rgba(239,68,68,0.1)' },
];


/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function riskColor(score: number): string {
  if (score <= 25) return '#10b981';
  if (score <= 50) return '#f59e0b';
  if (score <= 75) return '#f97316';
  return '#ef4444';
}

function categoryClass(cat: string): string {
  const c = cat?.toLowerCase() || 'low';
  if (c === 'low') return 'dp-badge--low';
  if (c === 'moderate') return 'dp-badge--moderate';
  if (c === 'high') return 'dp-badge--high';
  return 'dp-badge--critical';
}

function capitalize(s: string): string {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function methodLabel(m: string): string {
  if (m === 'ml') return 'ML-Powered';
  if (m === 'hybrid') return 'Hybrid AI';
  return 'Rule-Based';
}

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// SVG ring calculations (r=45, circumference ~ 283)
const RING_RADIUS = 45;
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS; // ~282.74


/* ================================================================== */
/*  COMPONENT                                                          */
/* ================================================================== */

const DiseasePrediction: React.FC = () => {
  const { t } = useTranslation(['services', 'common']);
  const [profile, setProfile] = useState<DiseaseRiskProfile | null>(null);
  const [history, setHistory] = useState<HistoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [predicting, setPredicting] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [aiExpanded, setAiExpanded] = useState(true);
  const [methodologyExpanded, setMethodologyExpanded] = useState(false);
  const [expandedDiseases, setExpandedDiseases] = useState<Record<string, boolean>>({});

  const toggleDiseaseExpand = (key: string) => {
    setExpandedDiseases(prev => ({ ...prev, [key]: !prev[key] }));
  };

  /* --- Fetch latest prediction ------------------------------------ */
  const fetchLatest = useCallback(async () => {
    try {
      const res = await axiosInstance.get(API_ENDPOINTS.diseasePrediction.history);
      const list: HistoryItem[] = res.data?.results ?? res.data ?? [];
      setHistory(list.slice(0, 5));

      if (list.length > 0) {
        // Fetch full details of latest prediction
        try {
          const detailRes = await axiosInstance.get(API_ENDPOINTS.diseasePrediction.latest);
          setProfile(detailRes.data);
        } catch {
          // Fallback: no detail available
          setProfile(null);
        }
      } else {
        setProfile(null);
      }
    } catch (err: any) {
      // If history gives 404, means no predictions yet
      if (err?.response?.status === 404) {
        setProfile(null);
        setHistory([]);
      } else {
        setError(t('services:diseasePrediction.failedToLoadRiskData'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLatest();
  }, [fetchLatest]);

  /* --- Run assessment --------------------------------------------- */
  const runAssessment = useCallback(async (includeAi = false) => {
    if (includeAi) {
      setAiLoading(true);
    } else {
      setPredicting(true);
    }
    setError(null);

    try {
      const payload = includeAi ? { include_ai: true } : {};
      const res = await axiosInstance.post(API_ENDPOINTS.diseasePrediction.predict, payload);
      setProfile(res.data);
      // Refresh history
      try {
        const hRes = await axiosInstance.get(API_ENDPOINTS.diseasePrediction.history);
        const list: HistoryItem[] = hRes.data?.results ?? hRes.data ?? [];
        setHistory(list.slice(0, 5));
      } catch { /* ignore */ }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.response?.data?.detail || t('services:diseasePrediction.assessmentFailed');
      setError(msg);
    } finally {
      setPredicting(false);
      setAiLoading(false);
    }
  }, []);


  /* ================================================================
     RENDER HELPERS
     ================================================================ */

  /* --- Loading skeleton ------------------------------------------- */
  const renderLoading = () => (
    <div className="dp-page">
      <div className="dp-skeleton dp-skeleton-hero" />
      <Container>
        <div className="dp-skeleton dp-skeleton-overall" />
        <div className="dp-skeleton-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="dp-skeleton dp-skeleton-card" />
          ))}
        </div>
      </Container>
    </div>
  );

  /* --- Error state ------------------------------------------------ */
  const renderError = () => (
    <div className="dp-page">
      {renderHero()}
      <Container>
        {renderDisclaimer()}
        <div className="dp-error-card">
          <div className="dp-error-icon">
            <FontAwesomeIcon icon={faExclamationTriangle} />
          </div>
          <h3 className="dp-error-title">{t('services:diseasePrediction.somethingWentWrong')}</h3>
          <p className="dp-error-text">{error}</p>
          <button className="dp-error-btn" onClick={() => { setError(null); setLoading(true); fetchLatest(); }}>
            <FontAwesomeIcon icon={faRotateRight} />
            {t('services:diseasePrediction.tryAgain')}
          </button>
        </div>
      </Container>
    </div>
  );

  /* --- Hero banner ------------------------------------------------ */
  const renderHero = () => (
    <div className="dp-hero-banner">
      <Container>
        <BackButton />
        <div className="dp-hero-layout">
          <div className="dp-hero-text">
            <h1 className="dp-hero-title">
              <span className="dp-hero-title-icon">
                <FontAwesomeIcon icon={faShieldHalved} />
              </span>
              {t('services:diseasePrediction.heroTitle')}
            </h1>
            <p className="dp-hero-subtitle">
              {t('services:diseasePrediction.heroSubtitle')}
            </p>
          </div>
          <div className="dp-hero-actions">
            <button
              className="dp-hero-btn primary"
              onClick={() => runAssessment(false)}
              disabled={predicting || aiLoading}
            >
              {predicting ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faPlayCircle} />
              )}
              {predicting ? t('services:diseasePrediction.analyzing') : (profile ? t('services:diseasePrediction.reRunAssessment') : t('services:diseasePrediction.runAssessment'))}
            </button>
          </div>
        </div>
      </Container>
    </div>
  );

  /* --- Disclaimer banner ------------------------------------------ */
  const renderDisclaimer = () => (
    <div className="dp-disclaimer">
      <div className="dp-disclaimer-icon">
        <FontAwesomeIcon icon={faTriangleExclamation} />
      </div>
      <p className="dp-disclaimer-text">
        {t('services:diseasePrediction.disclaimer')}
      </p>
    </div>
  );

  /* --- Welcome / empty state -------------------------------------- */
  const renderWelcome = () => (
    <div className="dp-page">
      {renderHero()}
      <Container>
        {renderDisclaimer()}
        <div className="dp-welcome-card">
          <div className="dp-welcome-icon-wrapper">
            <FontAwesomeIcon icon={faShieldHalved} />
          </div>
          <h2 className="dp-welcome-title">{t('services:diseasePrediction.runFirstAssessment')}</h2>
          <p className="dp-welcome-text">
            {t('services:diseasePrediction.welcomeDesc')}
          </p>
          <div className="dp-welcome-features">
            <div className="dp-welcome-feature">
              <span className="dp-welcome-feature-icon" style={{ background: 'rgba(6,182,212,0.1)', color: '#06b6d4' }}>
                <FontAwesomeIcon icon={faChartLine} />
              </span>
              {t('services:diseasePrediction.diseaseCategories')}
            </div>
            <div className="dp-welcome-feature">
              <span className="dp-welcome-feature-icon" style={{ background: 'rgba(139,92,246,0.1)', color: '#8b5cf6' }}>
                <FontAwesomeIcon icon={faMicrochip} />
              </span>
              {t('services:diseasePrediction.aiPoweredAnalysis')}
            </div>
            <div className="dp-welcome-feature">
              <span className="dp-welcome-feature-icon" style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981' }}>
                <FontAwesomeIcon icon={faLightbulb} />
              </span>
              {t('services:diseasePrediction.personalizedInsights')}
            </div>
          </div>
          <button
            className="dp-welcome-btn"
            onClick={() => runAssessment(false)}
            disabled={predicting}
          >
            {predicting ? (
              <FontAwesomeIcon icon={faSpinner} spin />
            ) : (
              <FontAwesomeIcon icon={faPlayCircle} />
            )}
            {predicting ? t('services:diseasePrediction.analyzingHealthData') : t('services:diseasePrediction.runAssessment')}
          </button>
        </div>
      </Container>
    </div>
  );

  /* --- Overall risk card ------------------------------------------ */
  const renderOverallCard = () => {
    if (!profile) return null;
    const score = profile.overall_risk_score ?? 0;
    const color = riskColor(score);
    const offset = RING_CIRCUMFERENCE - (score / 100) * RING_CIRCUMFERENCE;

    return (
      <div className="dp-overall-card">
        <div className="dp-overall-layout">
          <div className="dp-overall-ring-wrapper">
            <svg className="dp-overall-ring" viewBox="0 0 100 100">
              <circle className="dp-overall-ring-bg" cx="50" cy="50" r={RING_RADIUS} />
              <circle
                className="dp-overall-ring-progress"
                cx="50" cy="50" r={RING_RADIUS}
                stroke={color}
                strokeDasharray={RING_CIRCUMFERENCE}
                strokeDashoffset={offset}
              />
            </svg>
            <div className="dp-overall-ring-label">
              <span className="dp-overall-ring-score" style={{ color }}>{Math.round(score)}</span>
              <span className="dp-overall-ring-max">/ 100</span>
            </div>
          </div>

          <div className="dp-overall-info">
            <h2 className="dp-overall-heading">{t('services:diseasePrediction.overallHealthRiskScore')}</h2>
            <p className="dp-overall-subheading">
              {t('services:diseasePrediction.overallSubheading')}
            </p>

            <div className="dp-badges">
              <span className={`dp-badge ${categoryClass(profile.overall_risk_category)}`}>
                {capitalize(profile.overall_risk_category)} Risk
              </span>
              <span className="dp-badge dp-badge--method">
                <FontAwesomeIcon icon={faMicrochip} />
                {methodLabel(profile.prediction_method)}
              </span>
              {profile.ml_confidence != null && (
                <span className="dp-badge dp-badge--ml">
                  {Math.round(profile.ml_confidence * 100)}% ML Confidence
                </span>
              )}
            </div>

            <div className="dp-overall-date">
              <FontAwesomeIcon icon={faCalendarDays} />
              {t('services:diseasePrediction.lastAssessed', { date: formatDateTime(profile.created_at) })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* --- Score Methodology Section ----------------------------------- */
  const renderMethodology = () => {
    if (!profile) return null;
    const totalWeight = profile.total_weight ?? 10.9;
    const backendWeights = profile.disease_weights;

    return (
      <div className="dp-methodology-section">
        <div
          className="dp-methodology-toggle"
          onClick={() => setMethodologyExpanded(!methodologyExpanded)}
        >
          <div className="dp-methodology-toggle-left">
            <div className="dp-methodology-toggle-icon">
              <FontAwesomeIcon icon={faCircleInfo} />
            </div>
            <span className="dp-methodology-toggle-title">
              {t('services:diseasePrediction.howScoreCalculated')}
            </span>
          </div>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`dp-methodology-chevron ${methodologyExpanded ? 'open' : ''}`}
          />
        </div>

        {methodologyExpanded && (
          <div className="dp-methodology-body">
            {/* Explanation */}
            <p className="dp-methodology-desc">
              {t('services:diseasePrediction.howScoreCalculatedDesc')}
            </p>
            <p className="dp-methodology-desc">
              {t('services:diseasePrediction.eachDiseaseEvaluated')}
            </p>

            {/* Risk Level Legend */}
            <div className="dp-legend-section">
              <h4 className="dp-legend-title">
                <FontAwesomeIcon icon={faScaleBalanced} />
                {t('services:diseasePrediction.riskLevelLegend')}
              </h4>
              <div className="dp-legend-grid">
                {RISK_THRESHOLDS.map(th => (
                  <div className="dp-legend-item" key={th.label}>
                    <div className="dp-legend-color" style={{ background: th.color }} />
                    <div className="dp-legend-info">
                      <span className="dp-legend-label" style={{ color: th.color }}>{th.label}</span>
                      <span className="dp-legend-range">{th.min}–{th.max}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Disease Contributions Table */}
            <div className="dp-contributions-section">
              <h4 className="dp-contributions-title">
                <FontAwesomeIcon icon={faChartLine} />
                {t('services:diseasePrediction.diseaseContributions')}
              </h4>
              <div className="dp-contributions-table">
                <div className="dp-contributions-header">
                  <span>Disease</span>
                  <span>{t('services:diseasePrediction.diseaseScoreLabel')}</span>
                  <span>{t('services:diseasePrediction.weightLabel')}</span>
                  <span>{t('services:diseasePrediction.scoreTimesWeight')}</span>
                  <span>{t('services:diseasePrediction.contributionLabel')}</span>
                </div>
                {(() => {
                  let sumWeightedScore = 0;
                  const rows = DISEASES.map(d => {
                    const detail = profile.disease_details?.[d.key];
                    const score = detail?.score ?? (profile[d.riskField] as number | null) ?? 0;
                    const weight = detail?.weight ?? backendWeights?.[d.key] ?? 1.0;
                    const rawWeighted = detail?.weighted_score ?? Math.round(score * weight * 10) / 10;
                    const contribution = detail?.contribution ?? (Math.round((score * weight / totalWeight) * 10) / 10);
                    sumWeightedScore += rawWeighted;

                    return (
                      <div className="dp-contributions-row" key={d.key}>
                        <span className="dp-contributions-disease">
                          <FontAwesomeIcon icon={d.icon} style={{ color: d.color, marginRight: 8, fontSize: '0.8rem' }} />
                          {d.label}
                        </span>
                        <span style={{ color: riskColor(score), fontWeight: 700 }}>{Math.round(score)}</span>
                        <span className="dp-contributions-weight">×{weight}</span>
                        <span className="dp-contributions-raw">{rawWeighted.toFixed(1)}</span>
                        <span className="dp-contributions-weighted">{contribution.toFixed(1)}</span>
                      </div>
                    );
                  });

                  return (
                    <>
                      {rows}
                      <div className="dp-contributions-total">
                        <span>Overall Health Risk Score</span>
                        <span />
                        <span className="dp-contributions-total-weight">Σ {totalWeight.toFixed(1)}</span>
                        <span className="dp-contributions-total-raw">{sumWeightedScore.toFixed(1)}</span>
                        <span style={{ color: riskColor(profile.overall_risk_score ?? 0), fontWeight: 800 }}>
                          {Math.round(profile.overall_risk_score ?? 0)}
                        </span>
                      </div>
                    </>
                  );
                })()}
              </div>
              <p className="dp-contributions-note">
                {t('services:diseasePrediction.contributionNote', { totalWeight: totalWeight.toFixed(1) })}
              </p>
            </div>

            {/* Calculation Formula */}
            <div className="dp-formula-section">
              <h4 className="dp-formula-title">
                {t('services:diseasePrediction.calculationMethod')}
              </h4>
              <div className="dp-formula-box">
                <code>{t('services:diseasePrediction.calculationFormulaSimple')}</code>
              </div>
              <p className="dp-formula-technical">
                {t('services:diseasePrediction.calculationFormulaTechnical')}
              </p>
              {(profile.prediction_method === 'ml' || profile.prediction_method === 'hybrid') && (
                <p className="dp-formula-note">
                  {t('services:diseasePrediction.calculationFormulaAI')}
                </p>
              )}
            </div>

            {/* Term Glossary */}
            <div className="dp-glossary-section">
              <div className="dp-glossary-item">
                <span className="dp-glossary-term">{t('services:diseasePrediction.diseaseScoreLabel')}:</span>
                <span className="dp-glossary-def">{t('services:diseasePrediction.glossaryDiseaseScore')}</span>
              </div>
              <div className="dp-glossary-item">
                <span className="dp-glossary-term">{t('services:diseasePrediction.weightLabel')}:</span>
                <span className="dp-glossary-def">{t('services:diseasePrediction.glossaryWeight')}</span>
              </div>
              <div className="dp-glossary-item">
                <span className="dp-glossary-term">{t('services:diseasePrediction.scoreTimesWeight')}:</span>
                <span className="dp-glossary-def">{t('services:diseasePrediction.glossaryScoreTimesWeight')}</span>
              </div>
              <div className="dp-glossary-item">
                <span className="dp-glossary-term">{t('services:diseasePrediction.contributionLabel')}:</span>
                <span className="dp-glossary-def">{t('services:diseasePrediction.glossaryContribution')}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  /* --- Disease card ------------------------------------------------ */
  const renderDiseaseCard = (disease: DiseaseConfig) => {
    if (!profile) return null;
    const riskVal = profile[disease.riskField] as number | null;
    const score = riskVal ?? 0;
    const color = riskColor(score);
    const isExpanded = expandedDiseases[disease.key] || false;

    // Try to get detail from disease_details map
    const detail = profile.disease_details?.[disease.key];
    const category = detail?.category || (score <= 25 ? 'low' : score <= 50 ? 'moderate' : score <= 75 ? 'high' : 'critical');
    const method = detail?.method || profile.prediction_method;
    const factors = detail?.factors || [];
    const isML = method === 'ml' || method === 'hybrid';

    return (
      <div className={`dp-disease-card ${isExpanded ? 'dp-disease-card--expanded' : ''}`} key={disease.key}>
        <div className="dp-disease-card-header">
          <div className="dp-disease-card-left">
            <div className="dp-disease-icon" style={{ background: disease.bgColor, color: disease.color }}>
              <FontAwesomeIcon icon={disease.icon} />
            </div>
            <span className="dp-disease-name">{disease.label}</span>
          </div>
          <span className="dp-disease-score-num" style={{ color }}>{Math.round(score)}</span>
        </div>

        <div className="dp-disease-progress-wrapper">
          <div className="dp-disease-progress-track">
            <div
              className="dp-disease-progress-fill"
              style={{ width: `${score}%`, background: `linear-gradient(90deg, ${color}cc, ${color})` }}
            />
          </div>
        </div>

        <div className="dp-disease-meta">
          <span
            className={`dp-disease-category-chip ${categoryClass(category)}`}
          >
            {capitalize(category)}
          </span>
          {isML && (
            <span className="dp-badge dp-badge--ml">
              <FontAwesomeIcon icon={faMicrochip} style={{ fontSize: '0.65rem' }} />
              ML
            </span>
          )}
        </div>

        {factors.length > 0 && (
          <div className="dp-disease-factors">
            {factors.map((f, i) => (
              <span className="dp-disease-factor" key={i}>{f}</span>
            ))}
          </div>
        )}
      </div>
    );
  };

  /* --- AI Insights card ------------------------------------------- */
  const renderAIInsights = () => {
    if (!profile) return null;
    const hasExplanation = !!profile.ai_explanation;
    const hasRecommendations = profile.ai_recommendations?.length > 0;

    if (!hasExplanation && !hasRecommendations) {
      // Show "Get AI Analysis" prompt
      return (
        <div className="dp-ai-card">
          <div className="dp-ai-header" style={{ cursor: 'default' }}>
            <div className="dp-ai-header-left">
              <div className="dp-ai-header-icon">
                <FontAwesomeIcon icon={faRobot} />
              </div>
              <span className="dp-ai-header-title">{t('services:diseasePrediction.aiHealthInsights')}</span>
            </div>
          </div>
          <div className="dp-ai-body" style={{ textAlign: 'center', paddingTop: 8, paddingBottom: 32 }}>
            <p style={{ color: '#94a3b8', marginBottom: 16, fontSize: '0.9rem' }}>
              {t('services:diseasePrediction.getAiDesc')}
            </p>
            <button
              className="dp-ai-get-btn"
              onClick={() => runAssessment(true)}
              disabled={aiLoading || predicting}
            >
              {aiLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faRobot} />
              )}
              {aiLoading ? t('services:diseasePrediction.generatingInsights') : t('services:diseasePrediction.getAiAnalysis')}
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="dp-ai-card">
        <div className="dp-ai-header" onClick={() => setAiExpanded(!aiExpanded)}>
          <div className="dp-ai-header-left">
            <div className="dp-ai-header-icon">
              <FontAwesomeIcon icon={faRobot} />
            </div>
            <span className="dp-ai-header-title">{t('services:diseasePrediction.aiHealthInsights')}</span>
          </div>
          <FontAwesomeIcon
            icon={faChevronDown}
            className={`dp-ai-header-chevron ${aiExpanded ? 'open' : ''}`}
          />
        </div>

        {aiExpanded && (
          <div className="dp-ai-body">
            {hasExplanation && (
              <div className="dp-ai-explanation">{profile.ai_explanation}</div>
            )}
            {hasRecommendations && (
              <>
                <div className="dp-ai-recommendations-title">
                  <FontAwesomeIcon icon={faListCheck} style={{ color: '#10b981' }} />
                  {t('services:diseasePrediction.preventiveMeasures')}
                </div>
                <ul className="dp-ai-recommendations">
                  {profile.ai_recommendations.map((rec, i) => (
                    <li className="dp-ai-recommendation-item" key={i}>
                      <span className="dp-ai-recommendation-num">{i + 1}</span>
                      {rec}
                    </li>
                  ))}
                </ul>
              </>
            )}
            <button
              className="dp-ai-get-btn"
              style={{ marginTop: 20 }}
              onClick={() => runAssessment(true)}
              disabled={aiLoading || predicting}
            >
              {aiLoading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faRotateRight} />
              )}
              {aiLoading ? t('services:diseasePrediction.regenerating') : t('services:diseasePrediction.refreshAiAnalysis')}
            </button>
          </div>
        )}
      </div>
    );
  };

  /* --- Prediction history ----------------------------------------- */
  const renderHistory = () => {
    if (history.length === 0) return null;

    return (
      <div className="dp-history-section">
        <h3 className="dp-section-title">
          <span className="dp-section-title-icon" style={{ background: 'rgba(6,182,212,0.1)', color: '#06b6d4' }}>
            <FontAwesomeIcon icon={faCalendarDays} />
          </span>
          {t('services:diseasePrediction.predictionHistory')}
        </h3>
        <div className="dp-history-card">
          <div className="dp-history-timeline">
            {history.map((item, idx) => (
              <div className="dp-history-item" key={item.id}>
                <div className="dp-history-dot" />
                <span className="dp-history-date">{formatDate(item.created_at)}</span>
                <span className="dp-history-score" style={{ color: riskColor(item.overall_risk_score) }}>
                  {Math.round(item.overall_risk_score)}
                </span>
                <span className={`dp-badge ${categoryClass(item.overall_risk_category)}`}>
                  {capitalize(item.overall_risk_category)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };


  /* ================================================================
     MAIN RENDER
     ================================================================ */

  if (loading) return renderLoading();
  if (error && !profile) return renderError();
  if (!profile) return renderWelcome();

  return (
    <div className="dp-page">
      {renderHero()}
      <Container>
        {renderDisclaimer()}

        {error && (
          <div className="dp-error-card" style={{ marginBottom: 0 }}>
            <div className="dp-error-icon">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
            <h3 className="dp-error-title">{t('services:diseasePrediction.assessmentError')}</h3>
            <p className="dp-error-text">{error}</p>
            <button className="dp-error-btn" onClick={() => setError(null)}>
              <FontAwesomeIcon icon={faCheckCircle} />
              {t('services:diseasePrediction.dismiss')}
            </button>
          </div>
        )}

        {renderOverallCard()}
        {renderMethodology()}

        <h3 className="dp-section-title">
          <span className="dp-section-title-icon" style={{ background: 'rgba(99,102,241,0.1)', color: '#6366f1' }}>
            <FontAwesomeIcon icon={faChartLine} />
          </span>
          {t('services:diseasePrediction.diseaseRiskBreakdown')}
        </h3>
        <div className="dp-disease-grid">
          {DISEASES.map((d) => renderDiseaseCard(d))}
        </div>

        {renderAIInsights()}
        {renderHistory()}
      </Container>
    </div>
  );
};

export default DiseasePrediction;
